import { MouseEvent, useCallback } from 'react';

import { toast } from '#/components/UnifiedRfqs/toast';
import { useRfqQuotesCanceler } from '@paradigm/features/src/unified-rfqs/ui/rfqs/hooks';
import { useGrfqRfq } from '@paradigm/features/src/unified-rfqs/ui/rfq-context';
import { netPriceToPremium } from '@paradigm/features/src/unified-rfqs/utils/bbo-functions';
import { useHedgeNettingFeatureFlag } from '@paradigm/features/src/feature-flags/context';
import { unformatDecimal } from '@paradigm/utils/formatDecimal';
import { CrossButton } from '#/components/buttons/CrossButton';
import { useTrackAction } from '@paradigm/features/src/analytics';

interface Props {
  readonly title: string;
  readonly side: 'BUY' | 'SELL';
  /* if price is null the button kills all quotes from a given side */
  readonly price: string | null;
  disabled?: boolean;
}

export default function KillQuoteButton(props: Props) {
  const { title, side, disabled = false, ...restProps } = props;
  const rfq = useGrfqRfq();
  const hedgeNettingEnabled = useHedgeNettingFeatureFlag();
  // With hedge netting on the button gets the net display price while
  // quotes are stored and cancelled on the premium, so convert before
  // matching. Off, the displayed price is the premium already.
  const rawPrice = props.price != null ? unformatDecimal(props.price) : null;
  const price =
    rawPrice != null && hedgeNettingEnabled
      ? netPriceToPremium(rawPrice, rfq.legs)
      : rawPrice;

  const [{ isLoading }, cancel] = useRfqQuotesCanceler({
    rfqId: rfq.id,
    side,
    price,
  });

  const trackAction = useTrackAction();
  const handleClick = useCallback(
    (event: MouseEvent<HTMLButtonElement>) => {
      trackAction('kill quote', { side });
      event.stopPropagation();
      cancel(toast);
    },
    [cancel, side, trackAction],
  );

  return (
    <CrossButton
      {...restProps}
      title={title}
      onClick={handleClick}
      disabled={isLoading || disabled}
    />
  );
}
